import Big from "big.js";

import { getDecimalPlacesAndStep } from "components/Form/FormikControls/lib";

import {
  businessRelations,
  businessRelationsRateTypes,
} from "constants/businesses";

import { formatPercentage, getDollarPriceStringFromCents } from "lib";

export interface RelationshipDetail {
  id: string;
  relatedToName: string;
  relationType: string;
  rateType: string | null;
  rate: string | null;
  text: string;
}

const UNKNOWN_BUSINESS_NAME = "(Unknown Business)";

export const RELATIONSHIP_SEPARATOR = " · ";

const RELATION_TYPE_ORDER = [
  businessRelations.STOCK_AGENT,
  businessRelations.REBATE_AGENT,
  businessRelations.LISTING_AGENT,
  businessRelations.OUTSIDE_AGENT,
  businessRelations.BUYERS_PURCHASE_REBATE_AGENT,
];

const LISTING_FIRST_RELATION_TYPE_ORDER = [
  businessRelations.LISTING_AGENT,
  ...RELATION_TYPE_ORDER.filter(
    relationType => relationType !== businessRelations.LISTING_AGENT,
  ),
];

export const formatRelationshipRate = (
  relationship: Pick<BusinessRelation, "rateType" | "rateValue">,
): string | null => {
  if (relationship.rateType === businessRelationsRateTypes.PERCENTAGE) {
    const rateValuePercentage = new Big(relationship.rateValue).times(100);
    const { decimalPlacesToShow } = getDecimalPlacesAndStep(
      rateValuePercentage,
      2,
      4,
    );
    return `${formatPercentage(Number(relationship.rateValue), { decimalPlaces: decimalPlacesToShow, roundingMethod: null })}`;
  } else if (relationship.rateType === businessRelationsRateTypes.HEAD) {
    return `$${getDollarPriceStringFromCents(Number(relationship.rateValue))}/head`;
  } else {
    return null;
  }
};

// Mirrors backend BusinessRelation.score_for_livestock_sale (what billing
// applies). Not bestFitForSale: that requires a species match to also match sale type.
export const scoreRelationForSale = (
  relation: SaleContext,
  sale: Pick<SaleBase, "sale_sub_type_id" | "species_id" | "sale_type">,
): number => {
  if (!relation.saleSubTypeId && !relation.speciesId && !relation.saleType) {
    return 1;
  }

  let score = 0;
  if (relation.saleSubTypeId) {
    if (relation.saleSubTypeId !== sale.sale_sub_type_id) {
      return 0;
    }
    score += 8;
  }
  if (relation.speciesId) {
    if (relation.speciesId !== sale.species_id) {
      return 0;
    }
    score += 4;
  }
  if (relation.saleType) {
    if (relation.saleType !== sale.sale_type) {
      return 0;
    }
    score += 2;
  }
  return score;
};

export const bestRelationsForSale = <T extends BusinessRelation>(
  relations: T[],
  sale: Pick<SaleBase, "sale_sub_type_id" | "species_id" | "sale_type">,
): T[] => {
  const bestByKey: Record<string, { score: number; relation: T }> = {};
  for (const relation of relations) {
    const key = `${relation.relationType}|${relation.relatedToId}`;
    const score = scoreRelationForSale(relation, sale);
    if (score > 0 && score > (bestByKey[key]?.score || 0)) {
      bestByKey[key] = { score, relation };
    }
  }
  return Object.values(bestByKey).map(({ relation }) => relation);
};

const relationTypeRank = (relationType: string, listingFirst: boolean) => {
  const order = listingFirst
    ? LISTING_FIRST_RELATION_TYPE_ORDER
    : RELATION_TYPE_ORDER;
  const index = order.indexOf(relationType);
  return index === -1 ? order.length : index;
};

export const formatRelationshipScope = (
  relation: SaleContext,
  speciesById: Record<number, Species>,
  saleSubTypeById: Record<number, SaleSubType>,
): string | null =>
  [
    speciesById[relation.speciesId]?.name,
    relation.saleType,
    saleSubTypeById[relation.saleSubTypeId]?.name,
  ]
    .filter(Boolean)
    .join(" - ") || null;

export const describeRelationship = (
  relation: BusinessRelation,
  relatedToName: string,
  scope: string | null = null,
): string => {
  const terms = [
    relation.relationType,
    formatRelationshipRate(relation),
    scope && `(${scope})`,
  ]
    .filter(Boolean)
    .join(" ");
  return `${relatedToName} [${terms}]`;
};

interface BuildRelationshipDetailsOptions {
  listingFirst?: boolean;
  speciesById?: Record<number, Species>;
  saleSubTypeById?: Record<number, SaleSubType>;
}

export const buildRelationshipDetails = (
  relations: BusinessRelation[],
  businessById: Record<string, Business>,
  {
    listingFirst = false,
    speciesById,
    saleSubTypeById,
  }: BuildRelationshipDetailsOptions = {},
): RelationshipDetail[] =>
  relations
    .map(relation => {
      const relatedTo = businessById[relation.relatedToId];
      const relatedToName =
        relatedTo?.publicDisplayName ||
        relatedTo?.name ||
        UNKNOWN_BUSINESS_NAME;
      const scope =
        speciesById && saleSubTypeById
          ? formatRelationshipScope(relation, speciesById, saleSubTypeById)
          : null;
      return {
        id: relation.id,
        relatedToName,
        relationType: relation.relationType,
        rateType: relation.rateType,
        rate: formatRelationshipRate(relation),
        text: describeRelationship(relation, relatedToName, scope),
      };
    })
    .sort(
      (a, b) =>
        relationTypeRank(a.relationType, listingFirst) -
          relationTypeRank(b.relationType, listingFirst) ||
        a.relationType.localeCompare(b.relationType) ||
        a.relatedToName.localeCompare(b.relatedToName),
    );

export const relationshipFilterValues = (
  details: RelationshipDetail[],
): string[] =>
  details.flatMap(({ relationType, rateType, rate, relatedToName }) =>
    [relationType, rateType, rate, relatedToName].filter(Boolean),
  );

export const summariseRelationships = (
  texts: string[],
  maxShown: number = 2,
): string | null => {
  if (texts.length === 0) {
    return null;
  }
  const shown = texts.slice(0, maxShown);
  const hiddenCount = texts.length - maxShown;
  return (hiddenCount > 0 ? [...shown, `+${hiddenCount} more`] : shown).join(
    RELATIONSHIP_SEPARATOR,
  );
};
